import { useState } from 'react';
import { Button, Stack, Text, Tooltip } from '@mantine/core';
import { IconDownload } from '@tabler/icons-react';
import { useCsvDownload, type CsvQuery } from '../csv';

interface CsvDownloadButtonProps {
  /** Reporting-module path, e.g. '/reporting/stock-on-hand'. */
  path: string;
  query: CsvQuery;
  filename: string;
  label?: string;
  disabled?: boolean;
  /** Tooltip copy saying what the download contains — every report screen passes
   * its own (e.g. "Exports every row of this movement ledger, matching your
   * current filters"), since the same button is shared across all 12 reports. */
  description?: string;
}

/** The "Download CSV" button every report screen shows (BUILD_SPEC.md §13). Owns
 * its own pending/error state so a slow or failed download never blocks the rest
 * of the screen. */
export function CsvDownloadButton({
  path,
  query,
  filename,
  label = 'Download CSV',
  disabled,
  description = 'Exports the rows currently shown, matching your filters, as a CSV file',
}: CsvDownloadButtonProps) {
  const download = useCsvDownload();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleClick() {
    setIsLoading(true);
    setError(null);
    try {
      await download(path, query, filename);
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Download failed');
    } finally {
      setIsLoading(false);
    }
  }

  return (
    <Stack gap={4} align="flex-end">
      <Tooltip label={description} openDelay={300}>
        <span>
          <Button
            variant="light"
            leftSection={<IconDownload size={16} />}
            loading={isLoading}
            disabled={disabled}
            onClick={handleClick}
          >
            {label}
          </Button>
        </span>
      </Tooltip>
      {error && (
        <Text size="xs" c="red">
          {error}
        </Text>
      )}
    </Stack>
  );
}
